// How far an order has got, step by step. Shared by the customer's tracking
// page and the admin's order page, so the two can't disagree about whether
// something has happened.
import type { Order, OrderFulfillment, OrderStatus } from '../types';

// The one "ready" status, worded for how this order reaches the customer.
// Shipping orders skip it, but keep the stock wording if one lands there.
const READY_LABEL: Record<OrderFulfillment, string> = {
  pickup: 'Ready for pickup',
  dropoff: 'Ready for drop-off',
  shipping: 'Ready for pickup',
  none: 'Ready',
};

/** A status's label from `labels`, with ready_for_pickup worded for this order. */
export const statusLabel = (
  labels: Record<OrderStatus, string>,
  status: OrderStatus,
  fulfillment: OrderFulfillment = 'pickup'
): string =>
  status === 'ready_for_pickup' ? READY_LABEL[fulfillment] : labels[status] ?? status;

/**
 * Whether a step has happened, judged on its own evidence rather than on
 * where `status` currently sits.
 *
 * The shop doesn't always work in this order — an item is often made
 * before the invoice goes out — and reading position off the current
 * status meant sending the invoice afterwards un-ticked "Being made",
 * which is plainly wrong to anyone looking at it.
 *
 * Each milestone is stamped the first time it's reached and never
 * cleared. Orders from before those stamps existed fall back to inferring
 * it from the current status, which is right for anything that went
 * through the steps in order.
 */
export const stepReached = (order: Order, step: OrderStatus): boolean => {
  const atOrPast = (...statuses: OrderStatus[]) => statuses.includes(order.status);
  switch (step) {
    case 'created':
      return true;
    case 'invoice_sent':
      // Deliberately not inferred from a later status. An order can be
      // made, and even paid in person, without an invoice ever going out —
      // ticking it then would claim we'd sent something we hadn't. Only
      // evidence of an actual send counts: an invoice number is assigned on
      // the first send and never reissued, which covers orders predating
      // invoice_sent_at.
      return Boolean(order.invoice_sent_at || order.invoice_number);
    case 'paid':
      return Boolean(order.paid_at) || atOrPast('paid');
    case 'in_progress':
      return (
        Boolean(order.in_progress_at || order.ready_at || order.shipped_at || order.completed_at) ||
        atOrPast('in_progress', 'ready_for_pickup', 'shipped', 'completed')
      );
    case 'ready_for_pickup':
      return (
        Boolean(order.ready_at || order.completed_at) || atOrPast('ready_for_pickup', 'completed')
      );
    case 'shipped':
      return Boolean(order.shipped_at || order.completed_at) || atOrPast('shipped', 'completed');
    case 'completed':
      return Boolean(order.completed_at) || atOrPast('completed');
    default:
      return false;
  }
};

/** When a step was first reached, if the order recorded it. */
export const stepDate = (order: Order, step: OrderStatus): string | undefined => {
  switch (step) {
    case 'created':
      return order.created_at;
    case 'invoice_sent':
      return order.invoice_sent_at;
    case 'paid':
      return order.paid_at;
    case 'in_progress':
      return order.in_progress_at;
    case 'ready_for_pickup':
      return order.ready_at;
    case 'shipped':
      return order.shipped_at;
    case 'completed':
      return order.completed_at;
    default:
      return undefined;
  }
};

/**
 * Every step an order of this kind goes through, start to finish. A pickup
 * order is made ready, a shipping order is shipped; never both.
 * Cancelled and refunded are side branches, not steps.
 */
export const fullTimeline = (order: Order): OrderStatus[] => [
  'created',
  'invoice_sent',
  'paid',
  'in_progress',
  order.fulfillment === 'shipping' ? 'shipped' : 'ready_for_pickup',
  'completed',
];
